package org.mkui.canvas

import com.macrofocus.common.collection.CollectionFactory
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.crossplatform.client.layer.ElementalAveragingGWTLayer
import com.macrofocus.crossplatform.client.layer.ElementalDensityGWTLayer
import org.mkui.component.CPInteractiveComponent
import org.mkui.graphics.*
import org.mkui.graphics.pressure.Pressure
import web.canvas.CanvasRenderingContext2D
import web.canvas.ID
import web.canvas.OffscreenCanvas
import web.canvas.OffscreenCanvasRenderingContext2D

actual open class CPCanvas : CPInteractiveComponent {
    val layers = CollectionFactory.copyOnWriteArrayList<CanvasRenderingContext2DLayer>()
    private val drawings: MutableMap<IDrawing, IDrawingListener> = CollectionFactory.concurrentHashMap()

    private val listeners: MutableList<CanvasListener> = ArrayList()

    actual val showTiming : MutableProperty<Boolean> = SimpleProperty<Boolean>(false)

    val canvas = FunctionalCPCanvas(layers, listeners, showTiming)
    override val nativeComponent = canvas.nativeComponent

    actual val igraphics: IGraphics
        get() {
            val ctx = canvas.ctx
            if(ctx != null) {
                return CanvasRenderingContext2DIGraphics(ctx.unsafeCast<org.w3c.dom.CanvasRenderingContext2D>())
            } else {
                return CanvasRenderingContext2DIGraphics(OffscreenCanvas(100.0, 100.0).getContext(OffscreenCanvasRenderingContext2D.ID).unsafeCast<org.w3c.dom.CanvasRenderingContext2D>())
            }
        }

    actual fun addLayer(d: GraphicsContextDrawing) {
        layers.add(object : CanvasRenderingContext2DLayer {
            override fun prepare(width: Int, height: Int) {

            }

            override fun render(g: CanvasRenderingContext2D) {
                d.draw(CPGraphicsContext2D(g), getWidth(), getHeight())
            }
        })
    }

    actual open fun addDensityLayer(rendering: Rendering?, iDrawing: IDrawing, pressure: Pressure?, paletteProvider: PaletteProvider?) {
        val layer: CanvasRenderingContext2DLayer
        when (rendering) {
            Rendering.Density -> {
                layer = ElementalDensityGWTLayer(iDrawing, 0, 0, true, pressure!!, paletteProvider!!)
                layers.add(layer)
            }
            Rendering.AlphaBlended -> {
                val alpha = 0.5
                layer = ElementalBufferedGWTLayer(iDrawing, paletteProvider!!.palette.getColor(1.0), alpha)
                layers.add(layer)
            }
            Rendering.Opaque -> {
                layer = ElementalBufferedGWTLayer(iDrawing, paletteProvider!!.palette.getColor(1.0), null)
                layers.add(layer)
            }
            Rendering.Print -> {
                layer = ElementalDirectGWTLayer(iDrawing)
                layers.add(layer)
            }
            else -> throw IllegalArgumentException()
        }

        registerDrawing(iDrawing, object : IDrawingListener {
            override fun iDrawingChanged() {
                canvas.schedulePrepare(layer)
            }
        })
        canvas.schedulePrepare(layer)
    }

    actual open fun addAveragingLayer(rendering: Rendering?, iDrawing: IDrawing) {
//        nativeComponent.addAveragingLayer(rendering, iDrawing)
        val layer: CanvasRenderingContext2DLayer
        when (rendering) {
            Rendering.Density -> {
                layer = ElementalAveragingGWTLayer(iDrawing, true)
                layers.add(layer)
            }
            Rendering.AlphaBlended -> {
                val alpha = 0.5
                layer = ElementalBufferedGWTLayer(iDrawing)
                layers.add(layer)
            }
            Rendering.Opaque -> {
                layer = ElementalBufferedGWTLayer(iDrawing)
                layers.add(layer)
            }
            Rendering.Print -> {
                layer = ElementalDirectGWTLayer(iDrawing)
                layers.add(layer)
            }
            else -> throw IllegalArgumentException()
        }

        registerDrawing(iDrawing, object : IDrawingListener {
            override fun iDrawingChanged() {
                canvas.schedulePrepare(layer)
            }
        })
        canvas.schedulePrepare(layer)
    }

    actual open fun addLayer(iDrawing: IDrawing) {
        val layer = ElementalDirectGWTLayer(iDrawing)
        layers.add(layer)

        registerDrawing(iDrawing, object : IDrawingListener {
            override fun iDrawingChanged() {
                canvas.schedulePrepare(layer)
            }
        })
        canvas.schedulePrepare(layer)
    }

    fun addNativeLayer(d: CanvasRenderingContext2DLayer) {
        layers.add(d)
    }

    actual open fun addBufferedLayer(iDrawing: IDrawing) {
        val layer = ElementalBufferedGWTLayer(iDrawing)
        layers.add(layer)

        registerDrawing(iDrawing, object : IDrawingListener {
            override fun iDrawingChanged() {
                canvas.schedulePrepare(layer)
            }
        })
        canvas.schedulePrepare(layer)
    }

    private fun registerDrawing(drawing: IDrawing, listener: IDrawingListener) {
        drawings[drawing] = listener
        drawing.addIDrawingListener(listener)
    }

    actual open fun removeAllLayers() {
        layers.clear()
        for ((key, value) in drawings) {
            key.removeIDrawingListener(value)
        }
        drawings.clear()
    }

    actual open fun getWidth(): Double {
        return canvas.width.toDouble()
    }

    actual open fun getHeight(): Double {
        return canvas.height.toDouble()
    }

    actual open fun addCanvasListener(l: CanvasListener) {
        listeners.add(l)
    }

    protected open fun notifySizeChanged(width: Int, height: Int) {
        for (listener in listeners) {
            listener.sizeChange(width, height)
        }
    }

    actual fun refresh() {
        canvas.schedulePrepare()
    }

    actual fun redraw() {
        canvas.redraw()
    }

    actual override fun addMouseListener(l: MouseListener) {
        canvas.addMouseListener(l)
    }

    actual override fun addMouseMotionListener(l: MouseMotionListener) {
        canvas.addMouseMotionListener(l)
    }

    actual override fun removeMouseListener(l: MouseListener) {
        canvas.removeMouseListener(l)
    }

    actual override fun removeMouseMotionListener(l: MouseMotionListener) {
        canvas.removeMouseMotionListener(l)
    }

    actual fun addMouseWheelListener(l: MouseWheelListener) {
        canvas.addMouseWheelListener(l)
    }

    actual fun addKeyListener(l: KeyListener) {
        canvas.addKeyListener(l)
    }

    actual fun addContextMenuListener(l: ContextMenuListener) {
        canvas.addContextMenuListener(l)
    }

    actual fun removeMouseWheelListener(l: MouseWheelListener) {
        canvas.removeMouseWheelListener(l)
    }

    actual fun removeKeyListener(l: KeyListener) {
        canvas.removeKeyListener(l)
    }

    actual fun removeContextMenuListener(l: ContextMenuListener) {
        canvas.removeContextMenuListener(l)
    }
}